import { X } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Sticky bottom action bar shown when 1+ rows are selected.
 *
 * Props:
 *  - count           number
 *  - onClear         () => void
 *  - actions         [{ label, icon?, tone?: 'default'|'destructive', onClick, disabled?, testId? }]
 *  - testId          string
 */
export default function BulkActionBar({ count, onClear, actions = [], testId = "bulk-action-bar" }) {
  if (!count) return null;
  return (
    <div
      className="fixed left-1/2 -translate-x-1/2 bottom-4 md:bottom-6 z-40 flex items-center gap-2 px-3 py-2 rounded-full shadow-2xl border bg-card/95 backdrop-blur ring-1 ring-primary/20 max-w-[calc(100vw-1rem)] overflow-x-auto fade-in-up"
      data-testid={testId}
    >
      <div className="flex items-center gap-2 shrink-0">
        <span
          className="inline-flex items-center justify-center min-w-[26px] h-[26px] rounded-full bg-primary text-primary-foreground text-xs font-semibold px-1.5"
          data-testid={`${testId}-count`}
        >
          {count}
        </span>
        <span className="text-xs font-medium text-foreground/80 hidden sm:inline">selected</span>
        <button
          onClick={onClear}
          className="text-muted-foreground hover:text-foreground p-1 rounded-full transition"
          aria-label="Clear selection"
          data-testid={`${testId}-clear`}
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="w-px h-6 bg-border shrink-0" />
      <div className="flex items-center gap-1.5 shrink-0">
        {actions.map((a, i) => (
          <Button
            key={i}
            size="sm"
            variant={a.tone === "destructive" ? "destructive" : "outline"}
            onClick={a.onClick}
            disabled={a.disabled}
            className="h-8 gap-1.5 whitespace-nowrap"
            data-testid={a.testId || `${testId}-action-${i}`}
          >
            {a.icon}
            <span>{a.label}</span>
          </Button>
        ))}
      </div>
    </div>
  );
}
